import type { ApiClient } from '../../../api';
import type { IssueStartResult, IssueTakeOverResult, StartSource } from '../types/domain.types';

import { CTL_ENDPOINTS } from './endpoints';
import { CtlApiError } from './errors';
import { request } from './http';
import { issueStartSchema, issueTakeOverSchema, parseOrThrow } from './schemas';

const NOT_OPEN_STATUS = 404;
const NOT_FOUND = { outcome: 'NOT_FOUND' } as const;

const claimBody = (source: StartSource, via: string | undefined) => (via === undefined ? { source } : { source, via });

const isNotOpen = (error: unknown): boolean => error instanceof CtlApiError && error.status === NOT_OPEN_STATUS;

const orNotFound = async <T>(claim: Promise<T>): Promise<T | typeof NOT_FOUND> => {
  try {
    return await claim;
  } catch (error) {
    if (isNotOpen(error)) return NOT_FOUND;
    throw error;
  }
};

export const issueClaimsWith = (client: ApiClient) => ({
  start: (issueId: string, source: StartSource, via?: string): Promise<IssueStartResult> =>
    orNotFound(
      client
        .request('post', CTL_ENDPOINTS.ISSUE_START(issueId), { body: claimBody(source, via) })
        .then((raw) => parseOrThrow(issueStartSchema, raw, 'issue start')),
    ),
  takeOver: (issueId: string, source: StartSource, via?: string): Promise<IssueTakeOverResult> =>
    orNotFound(
      client
        .request('post', CTL_ENDPOINTS.ISSUE_TAKE_OVER(issueId), { body: claimBody(source, via) })
        .then((raw) => parseOrThrow(issueTakeOverSchema, raw, 'issue take-over')),
    ),
});

export const { start: startIssue, takeOver: takeOverIssue } = issueClaimsWith({ request });
